import { useQuery } from "@apollo/client";
import { withErrorBoundary } from "@sentry/react";
import { sortBy } from "lodash";
import { useMemo } from "react";
import {
  GetDashboardInsightsQuery,
  GetDashboardInsightsQueryVariables,
  InsightType,
} from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import Insight from "@apps/dashboard-new/components/insight";
import getDashboardInsightsQuery from "@apps/dashboard-new/graphql/get-dashboard-insights-query";
import useUiPreferenceCache from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import Error from "@components/error/error";
import Layout from "@components/layout/layout";
import { assertEdgesNonNull } from "@helpers/helpers";

import { requestTypes } from "./insights-constants";

const InsightsComponent = ({ selectedUser }: { selectedUser: BasicUser }) => {
  const { uiPreferenceCache } = useUiPreferenceCache();
  const { data, loading } = useQuery<
    GetDashboardInsightsQuery,
    GetDashboardInsightsQueryVariables
  >(getDashboardInsightsQuery, {
    notifyOnNetworkStatusChange: true,
    skip: !uiPreferenceCache.dashboardRequirementsContainerExpanded,
    variables: { personId: selectedUser.id },
  });
  const allInsights = useMemo(() => {
    const userInsights = data?.user?.insights
      ? assertEdgesNonNull(data.user.insights)
      : [];
    // The "missing manager" insight is intentionally hidden on the homepage
    // (TF-1545). It still shows elsewhere (e.g. the dashboard).
    const visibleInsights = userInsights.filter(
      (insight) => insight.type !== InsightType.MissingManager,
    );
    return sortBy(visibleInsights, "type");
  }, [data]);

  const requirementInsights = useMemo(() => {
    return allInsights.filter(
      (insight) =>
        !!insight.complianceProgram?.id && !requestTypes.includes(insight.type),
    );
  }, [allInsights]);
  // TF-1554: The generic "Insights" bucket (insights with no compliance
  // program and no request type) is intentionally not rendered on the
  // homepage. Requirements and Requests are kept since there is no
  // notification engine surfacing them elsewhere yet.
  const requests = useMemo(() => {
    return allInsights.filter((insight) => requestTypes.includes(insight.type));
  }, [allInsights]);

  const loadingNoData = !data && loading;

  const countClassName =
    "px-1.5 py-0.5 bg-gray-100 rounded-md font-semibold text-xs text-gray-900 flex items-center gap-1";

  if (
    !loadingNoData &&
    requests.length === 0 &&
    requirementInsights.length === 0
  ) {
    return null;
  }

  const containerClassName = "max-h-48 overflow-y-auto p-4 border rounded-md";
  return (
    <>
      {requirementInsights.length > 0 && (
        <Layout.MainSubSection
          title={
            <div className="flex items-center gap-2">
              Requirements
              <span className={countClassName}>
                {requirementInsights.length}
              </span>
            </div>
          }
          loading={loadingNoData}
        >
          <div className={containerClassName}>
            {requirementInsights.map((requirementInsight) => (
              <div key={requirementInsight.id} className="p-2">
                <Insight
                  insight={requirementInsight}
                  selectedUser={selectedUser}
                />
              </div>
            ))}
          </div>
        </Layout.MainSubSection>
      )}
      {requests.length > 0 && (
        <Layout.MainSubSection
          title={
            <div className="flex items-center gap-2">
              Requests
              <span className={countClassName}>{requests.length}</span>
            </div>
          }
          loading={loadingNoData}
        >
          <div className={containerClassName}>
            {requests.map((request) => (
              <div key={request.id} className="p-2">
                <Insight insight={request} selectedUser={selectedUser} />
              </div>
            ))}
          </div>
        </Layout.MainSubSection>
      )}
    </>
  );
};

const InsightsWithSentry = withErrorBoundary(InsightsComponent, {
  fallback: <Error description={"The insights could not be rendered."} />,
});

export default InsightsWithSentry;
